import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCommonColumns } from "../../../../utils/hooks/useCommonColumns";
import { useCompanyDateFormat, useIsMasterCompany } from "../../../../utils/hooks/company";
import { dateTimeFormat } from "../../../../utils/helpers/date";
import { createColumnHelper } from "@tanstack/react-table";

const columnHelper = createColumnHelper();

export const useColumns = ({ edit, remove, clone, allocation, history, versions }) => {
    const { t } = useTranslation();
    const isMasterCompany = useIsMasterCompany();
    const dateFormat = useCompanyDateFormat();

    const { code, description, status } = useCommonColumns({ edit });

    return useMemo(() => {
        const columns = [
            { ...code, header: t("name") },
            description,
            status,
            columnHelper.accessor("accural.method", {
                header: t("method"),
                cell: (info) => t(info.getValue()),
            }),
        ];

        if (isMasterCompany) {
            columns.push(
                edit || remove || clone || allocation || versions
                    ? columnHelper.display({
                          id: "actions",
                          enableHiding: false,
                          enableColumnFilter: false,
                          header: <div className="text-right">{t("actions")}</div>,
                          cell: (info) => {
                              const { id, company, isDefault, scheduledChangeFrom } = info.row.original;

                              return (
                                  <div className="actions text-primary text-right">
                                      {allocation && (
                                          <span
                                              className="btn-inner--icon icon-sm cursor-pointer mr-3"
                                              onClick={() => allocation(id)}
                                          >
                                              <i className="fa-solid fa-users" />
                                          </span>
                                      )}
                                      {company && !isDefault && edit && (
                                          <span
                                              className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                              onClick={() => edit(id)}
                                          >
                                              <i className="fas fa-pen" />
                                          </span>
                                      )}

                                      {company && !isDefault && remove && (
                                          <span
                                              className="btn-inner--icon mr-3 icon-sm cursor-pointer"
                                              onClick={() => remove(id)}
                                          >
                                              <i className="fas fa-trash" />
                                          </span>
                                      )}

                                      {clone && (
                                          <span
                                              className="btn-inner--icon icon-sm cursor-pointer"
                                              onClick={() => clone(id)}
                                          >
                                              <i className="ni ni-single-copy-04" />
                                          </span>
                                      )}

                                      {versions && (
                                          <span
                                              className="btn-inner--icon icon-sm cursor-pointer ml-3 position-relative"
                                              onClick={() => versions(info.row.original)}
                                              title={
                                                  scheduledChangeFrom
                                                      ? t("time-off-plan-has-scheduled-change", {
                                                            date: dateTimeFormat(scheduledChangeFrom, dateFormat),
                                                        })
                                                      : t("time-off-plan-versions")
                                              }
                                          >
                                              <i className="fa-solid fa-code-compare" />
                                              {/* the plan has a scheduled change (a pending version) */}
                                              {scheduledChangeFrom && (
                                                  <span
                                                      className="position-absolute rounded-circle bg-warning"
                                                      style={{ top: -3, right: -5, width: 8, height: 8 }}
                                                  />
                                              )}
                                          </span>
                                      )}

                                    {history && 
                                        <span
                                            className="btn-inner--icon icon-sm cursor-pointer ml-3"
                                            onClick={() => history(id)}
                                            title={t("history")}
                                        >
                                            <i className="fa-solid fa-clock-rotate-left" />
                                        </span>                                        
                                    }
                                  </div>
                              );
                          },
                      })
                    : null
            );
        }
        return columns;
    }, [t, isMasterCompany, dateFormat, code, description, status, clone, edit, remove, allocation, history, versions]);
};
